import React from "react";
import App, { Container } from "next/app";
import Head from "next/head";
import Router from "next/router";
import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import { create } from "jss";
import { compose } from "recompose";
import { Provider } from "react-redux";
import { PageTransition } from "next-page-transitions";
import rtl from "jss-rtl";
import { StylesProvider, jssPreset } from "@material-ui/styles";
import { library } from "@fortawesome/fontawesome-svg-core";
import withRedux from "next-redux-wrapper";
import { faTwitter } from "@fortawesome/free-brands-svg-icons/faTwitter";
import { faFacebook } from "@fortawesome/free-brands-svg-icons/faFacebook";
import { faInstagram } from "@fortawesome/free-brands-svg-icons/faInstagram";
import { faPinterest } from "@fortawesome/free-brands-svg-icons/faPinterest";
import CssBaseline from "@material-ui/core/CssBaseline";
import Loading from "react-loading-bar";
import { appWithTranslation, i18n } from "../i18n";
import GlobalModal from "../components/GlobalModal";
import appTheme from "../theme/appTheme";
import store from "../reducers/store";
import brand from "../static/text/brand";
import { toAbsoluteAssetUrl } from "../components/head";
import "../static/css-import.css";
import "../node_modules/react-loading-bar/dist/index.css";
import "../node_modules/animate.css/animate.css";
import "../vendors/animate-extends.css";
import "../vendors/page-transition.css";
import "slick-carousel/slick/slick.css";
import LogoutModal from "../components/logout-modal";
import { getTokenUser, migrateLegacyCookies } from "../helpers/cookie";
import { languageParser } from "../helpers/general";
import {
  seetPaymentCountry, setGeolocationCookie
} from "../helpers/setCookie";
import ChooseCountryModal from "../components/ChooseCountryModal";
import {
  buildMetaAdvancedMatching,
  buildMetaTrackingPayload,
  buildPageMetadata,
  getMetaExternalId,
  initMetaPixel,
  trackPixelPageView
} from "../helpers/ads/conversion-tracking";
import { pushPageView } from "../helpers/ads/data-layer";
import { captureTikTokAttribution } from "../helpers/ads/tiktok-attribution";

let themeType = "light";
if (typeof Storage !== "undefined") {
  themeType = localStorage.getItem("luxiTheme") || "light";
}

class MyApp extends App {
  state = {
    loading: false,
    theme: {
      ...appTheme("myfitsociety", themeType),
      direction: i18n.language === "ar" ? "rtl" : "ltr"
    }
  }

  static async getInitialProps({ Component, ctx }) {
    // LIVE GEO WINS (VPN lag fix 2026-07-10):
    // Was: cookie-first getCFCountry lagged live cf-ipcountry -> mismatch 302
    // with empty pageProps SKIPPED Component.getInitialProps (Plan pricing) for
    // one reload. getCFCountry is now header-first (setCookie.js); always refresh
    // geo cookies here and CONTINUE (no 302) so Plan GIP prices live geo now.
    // Language (languageParser below) is unrelated and unchanged.
    if (ctx.req && ctx.req.headers["cf-ipcountry"]) {
      setGeolocationCookie(ctx.req, ctx.res);
    }

    try {
      if (ctx.req && ctx.req.headers["cf-ipcountry"]) {
        seetPaymentCountry(ctx.req, ctx.res);
      }

      const userToken = await getTokenUser(ctx);
      const appLanguage = languageParser(ctx);

      // if (userToken && !_.isEmpty(userToken)) {
      //   const additionalHeaders = { authorization: getTokenUser(ctx) };
      //   const body = {
      //     language_code: appLanguage
      //   };
      //   setInternalLanguage(body, additionalHeaders);
      // }
      return {
        pageProps: {
          ...(Component.getInitialProps ? await Component.getInitialProps(ctx) : {})
        },
        token: userToken,
        appLanguage
      };
    } catch (err) {
      // Never return without a `pageProps` key: next-i18next@1.x reads
      // `wrappedComponentProps.pageProps.namespacesRequired` and throws
      // "Cannot read property 'namespacesRequired' of undefined" otherwise,
      // which (after a redirect already wrote headers) surfaces as a
      // Cloudflare 520 (ERR_HTTP_HEADERS_SENT). Guard the re-call so a
      // throwing page getInitialProps can't bubble out a second time.
      let pageProps = {};
      try {
        pageProps = Component.getInitialProps
          ? (await Component.getInitialProps(ctx)) || {}
          : {};
      } catch (innerErr) {
        pageProps = {};
      }
      return { pageProps };
    }
  }

  // Meta Pixel: PageView is fired from _app only, with route + CMS metadata.
  // The _document loader only bootstraps fbq/init so server route variants get
  // one consistent client-side PageView payload.
  trackPageView = (url) => {
    const { pageProps, router } = this.props;
    const activeRouter = (Router && Router.router) || router;
    const query = (activeRouter && activeRouter.query) || {};
    const pageMetadata = buildPageMetadata({
      pageProps,
      router: activeRouter,
      url,
    });
    const trackingPayload = buildMetaTrackingPayload({ pageMetadata, query });
    // Attach external_id (stable anon visitor id pre-login) so PageView carries it
    // on both browser (plain) and server (CAPI) for the same event.
    const advancedMatching = buildMetaAdvancedMatching({
      externalId: getMetaExternalId(),
      country: trackingPayload.meta_country || trackingPayload.country_code || trackingPayload.country,
    });
    trackPixelPageView(trackingPayload, { applyAdvancedMatching: true, advancedMatching });
  }

  handleRouteChange = (url) => {
    setTimeout(() => this.trackPageView(url), 0);
    // SPA virtual page_view for GA4/GTM only on route changes (not initial load —
    // GTM All Pages already covers first paint). Meta PageView logic unchanged above.
    pushPageView({
      page_path: typeof url === "string" ? url : undefined,
    });
    // Persist ttclid/_ttp across SPA navigations for Events API match quality.
    try {
      const activeRouter = (Router && Router.router) || this.props.router;
      captureTikTokAttribution({ query: (activeRouter && activeRouter.query) || {} });
    } catch (e) { /* ignore */ }
  }

  componentDidMount() {
    migrateLegacyCookies();
    document.dir = i18n.language === "ar" ? "rtl" : "ltr";
    library.add(faTwitter, faFacebook, faInstagram, faPinterest);

    // Pastikan fbq siap untuk track event berikutnya (idempotent kalau loader global sudah jalan).
    initMetaPixel();
    try {
      const activeRouter = (Router && Router.router) || this.props.router;
      captureTikTokAttribution({ query: (activeRouter && activeRouter.query) || {} });
    } catch (e) { /* ignore */ }
    this.trackPageView();
    Router.events.on("routeChangeComplete", this.handleRouteChange);

    this.setState({ loading: true });
    this.loadingTimeout = setTimeout(() => {
      this.setState({ loading: false });
    }, 2000);

    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles.parentNode.removeChild(jssStyles);
    }
  }

  componentWillUnmount() {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }
    Router.events.off("routeChangeComplete", this.handleRouteChange);
  }

  toggleDarkTheme = () => {
    this.setState(prevState => {
      const newPaletteType = prevState.theme.palette.type === "light" ? "dark" : "light";
      localStorage.setItem("luxiTheme", newPaletteType);
      return {
        theme: {
          ...appTheme("myfitsociety", newPaletteType),
          direction: prevState.theme.direction,
        }
      };
    });
  }

  toggleDirection = dir => {
    document.dir = dir;
    this.setState(prevState => ({
      theme: {
        ...prevState.theme,
        direction: dir,
        palette: {
          ...prevState.theme.palette
        }
      }
    }));
  }

  render() {
    const { theme, loading } = this.state;
    const {
      Component, pageProps, router, store: reduxStore
    } = this.props;
    const muiTheme = createMuiTheme(theme);
    const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

    return (
      <Container>
        {/* Default description + twitter card/image via next/head — a page's own
            same-`name` tag wins the dedupe, so these never duplicate. No default
            twitter:title/description: crawlers fall back to each page's own og
            tags instead of the homepage copy. og:*
            defaults can't live here: Next 9 dedupes `name` keys only, not
            `property`, so og defaults stay in components/head.js (skipped per
            page via ownsOg). */}
        <Head>
          {/* The ONE viewport meta. A next/head viewport meta replaces
              Next's default (width=device-width,minimum-scale=1,initial-scale=1),
              so the page no longer ships two. No maximum-scale/user-scalable:
              pinch-zoom stays allowed. minimum-scale=1 enables GPU rasterization. */}
          <meta
            name="viewport"
            content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no"
          />
          <meta name="description" content={brand.mobile.desc} />
          <meta name="twitter:card" content="summary_large_image" />
          {/* summary_large_image needs a wide banner — mobile.img is a square
              512px logo that Twitter would crop badly; reuse the og banner. */}
          <meta name="twitter:image" content={toAbsoluteAssetUrl(brand.mobile.ogImg)} />
        </Head>
        <Provider store={reduxStore}>
          <StylesProvider jss={jss}>
            <MuiThemeProvider theme={muiTheme}>
              <CssBaseline />
              <Loading
                show={loading}
                color={theme.palette.primary.main}
                showSpinner={false}
              />
              <div id="main-wrap">
                <PageTransition timeout={300} classNames="page-fadeUp-transition" skipInitialTransition>
                  <Component
                    {...pageProps}
                    onToggleDark={this.toggleDarkTheme}
                    onToggleDir={this.toggleDirection}
                    key={router.route}
                  />
                </PageTransition>
                <GlobalModal />
                <LogoutModal />
                {/* <ChooseCountryModal /> */}
              </div>
            </MuiThemeProvider>
          </StylesProvider>
        </Provider>
      </Container>
    );
  }
}

const makeStore = () => store;

const enhancer = compose(
  withRedux(makeStore),
  appWithTranslation
);

export default enhancer(MyApp);
